import React, { useMemo } from 'react'
import styled from 'styled-components'
import Box from '@ui/Box'
import { FaCrown } from 'react-icons/fa'
import { createBillingPortalSession, usePlansInfo } from '@api/users'
import IconButton from '@ui/IconButton'
import { useRouter } from 'next/navigation'
import { Color } from '@interfaces/designSystem'
import { PaidFeatureType } from '@interfaces/common'
import { SubscriptionPlan } from '@interfaces/auth'
import { notReachable } from '@utils/notReachable'
import Grid from '@ui/Grid'

const Img = styled.img``
const Cover = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1001;
`

type Props = {
  feature: PaidFeatureType
  onClose: VoidFunction
}

export const UpgradeBanner = ({ feature, onClose }: Props) => {
  const router = useRouter()
  const { data: plansInfo, isLoading } = usePlansInfo()

  const checkNeededPlan = (): SubscriptionPlan => {
    if (!plansInfo) {
      return 'pro'
    }

    switch (feature) {
      case 'ab':
        return plansInfo.find(plan => plan.abIncluded)?.id || 'pro'

      case 'pricesByCountry':
        return plansInfo.find(plan => plan.pricesByCountryIncluded)?.id || 'pro'

      default:
        notReachable(feature)
    }

    return 'pro'
  }

  const getTitle = () => {
    switch (feature) {
      case 'ab':
        return 'Upgrade your plan to enable A/B testing'

      case 'pricesByCountry':
        return 'Upgrade your plan to specify different prices for different countries'

      default:
        return notReachable(feature)
    }
  }

  const isClosable = useMemo(() => {
    switch (feature) {
      case 'ab':
        return false

      case 'pricesByCountry':
        return true

      default:
        return notReachable(feature)
    }
  }, [feature])

  return (
    <Cover>
      <Box
        textAlign="center"
        bg={Color.White}
        p={30}
        borderRadius={20}
        zIndex={1001}
        style={{ boxShadow: '0px 0px 19px 0px rgba(0, 0, 0, 0.04)' }}
      >
        <Img src="/img/upgrade.svg" />
        <Box fontSize={28} mt={40} mb={32}>
          {getTitle()}
        </Box>
        <Grid flow="column" gap={16} justifyContent="center">
          <IconButton
            loading={isLoading}
            disabled={isLoading}
            type="primary"
            icon={FaCrown}
            size="large"
            onClick={async () => {
              const resp = await createBillingPortalSession(checkNeededPlan())
              router.push(resp.data.url)
            }}
          >
            Upgrade now
          </IconButton>
          {isClosable && (
            <IconButton size="large" type="default" onClick={onClose}>
              Cancel
            </IconButton>
          )}
        </Grid>
      </Box>
    </Cover>
  )
}
